// src/api/AuthContext.tsx

import React, { createContext, useState, useContext, useEffect, ReactNode, useRef } from "react"; // Added useRef
import * as Sentry from "@sentry/react";
// Renamed 'login' to 'apiLogin' to avoid confusion with the context's login function
import { getMe, login as apiLogin, googleLogin as apiGoogleLogin, logout as apiLogout } from "./auth";

interface User {
  id: string;
  fullName: string;
  email: string;
  avatar?: string;
  // ... any other user properties
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: any) => Promise<any>;
  googleLogin: (idToken: string) => Promise<any>;
  logout: (redirectTo?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: async () => { console.error("Login function not implemented") },
  googleLogin: async () => { console.error("Google login function not implemented") },
  logout: async () => { console.error("Logout function not implemented") },
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isRedirecting = useRef(false); // New ref to prevent multiple redirects

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.fullName,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  useEffect(() => {
    const checkUserStatus = async () => {
      const token = localStorage.getItem("token");
      try {
        // Cookie-first sessions: attempt to restore user even without a local token.
        const userData: User = await getMe();
        setUser(userData);
      } catch (error: any) {
        // If we had an explicit token and session is invalid, force logout.
        // If we are cookie-only (no token), just treat as guest without redirect loops.
        if (token) {
          console.error("[AuthContext] Session invalid/expired. Logging out.", error);
          void logout("/login");
        } else {
          setUser(null);
        }
      }
      setLoading(false);
    };
    
    // Prevent re-running checkUserStatus if a redirect is already in progress.
    // This also helps mitigate issues with React Strict Mode double-rendering during development.
    if (!isRedirecting.current) {
        checkUserStatus();
    }
  }, []); // Empty dependency array means this runs once on mount

  const login = async (credentials: any) => {
    setLoading(true); // Always set loading to true at the start
    try {
      const data = await apiLogin(credentials); // This will throw on 401 from backend
      
      const userData: User = await getMe(); // Re-fetch user data to ensure up-to-date
      setUser(userData);
      
      window.dispatchEvent(new Event("user-logged-in"));
      return data;
    } catch (error) {
      console.error("[AuthContext] Standard login attempt failed.", error);
      setUser(null);
      localStorage.removeItem("token"); // Remove potentially stale token
      throw error; // Re-throw for UI component to display message
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async (idToken: string) => {
    setLoading(true); // Set loading state for Google login as well
    try {
      const data = await apiGoogleLogin(idToken); // This will throw on 401 from backend
      
      const userData: User = await getMe(); // Re-fetch user data to ensure up-to-date
      setUser(userData);
      
      window.dispatchEvent(new Event("user-logged-in"));
      return data;
    } catch (error) {
      console.error("[AuthContext] Google login attempt failed.", error);
      setUser(null);
      localStorage.removeItem("token"); // Remove potentially stale token
      throw error; // Re-throw for UI component to display message
    } finally {
      setLoading(false);
    }
  };

  const logout = async (redirectTo: string = "/") => {
    setUser(null);
    localStorage.removeItem("token"); // Ensure token is cleared (avoid nuking unrelated localStorage)

    // Important: if we redirect immediately, the logout request can be aborted,
    // leaving the httpOnly cookie session active. Use a keepalive request and
    // wait briefly before redirecting.
    try {
      if (typeof fetch !== "undefined") {
        const { API_BASE_URL } = await import("../config/apiConfig");
        const csrf = (() => {
          if (typeof document === "undefined") return null;
          const cookies = document.cookie ? document.cookie.split(";") : [];
          for (const part of cookies) {
            const [rawKey, ...rest] = part.trim().split("=");
            if (rawKey === "csrf_token") return decodeURIComponent(rest.join("="));
          }
          return null;
        })();

        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: "POST",
          credentials: "include",
          keepalive: true,
          headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRF-Token": csrf } : {}),
          },
          body: "{}",
        });
      } else {
        await apiLogout();
      }
    } catch {
      // Best-effort; client state is already cleared.
    }

    if (!isRedirecting.current) {
      isRedirecting.current = true;
      window.location.href = redirectTo;
    }
  };

  const value = { user, loading, login, googleLogin, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};